<template>
  <div>
    <div align="right" style="margin: 10px">
      <Select v-model="currentPeriodKey" :datas="periodOptions" keyName="key" titleName="title"
              :width="160" placeholder="选择工资期间" @change="loadData"></Select>
      <Button color="primary" @click="openPeriodForm">新建期间</Button>
      <Button color="primary" @click="addRow" :disabled="!editable">添加员工行</Button>
      <Button color="green" @click="saveBills" :disabled="!editable" :loading="loading">保存工资单</Button>
      <Button color="green" @click="applySocial" :disabled="!period || period.status !== 1" :loading="loading">应用五险一金</Button>
      <Button color="green" @click="accruePreview" :disabled="!period || period.status !== 1" :loading="loading">计提工资</Button>
      <Button color="green" @click="doPay" v-if="period && period.status === 2" :loading="loading">发放工资</Button>
      <Button color="red" v-if="period && period.status === 1" @click="removePeriod">删除期间</Button>
      <span v-if="period" style="margin-left: 10px;font-weight: normal;">状态：{{ statusText(period.status) }}</span>
    </div>

    <div style="margin: 10px" v-if="!period">
      <p class="text-center">暂无工资期间，请先"新建期间"开档。</p>
    </div>

    <div style="margin: 10px; overflow-x: auto" v-else>
      <table class="salary-table">
        <thead>
        <tr>
          <th :width="150">员工</th>
          <th>基本工资</th>
          <th>奖金</th>
          <th>个人社保</th>
          <th>个人公积金</th>
          <th>公司社保</th>
          <th>公司公积金</th>
          <th>费用类型</th>
          <th>个税(自动)</th>
          <th>实发(自动)</th>
          <th :width="60" v-if="editable">操作</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, index) in bills" :key="index">
          <td>
            <template v-if="editable">
              <Select v-model="row.employeeId" :datas="rowEmployees(row)" keyName="id" titleName="name"
                      :filterable="true" :width="130"></Select>
            </template>
            <template v-else>{{ employeeName(row.employeeId) }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.gross" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.gross | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.bonus" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.bonus | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.socialPersonal" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.socialPersonal | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.fundPersonal" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.fundPersonal | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.socialCompany" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.socialCompany | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <NumberInput v-model="row.fundCompany" :min="0" :width="100"></NumberInput>
            </template>
            <template v-else>{{ row.fundCompany | numFormat }}</template>
          </td>
          <td>
            <template v-if="editable">
              <Select v-model="row.expenseType" :datas="expenseDict" keyName="key" titleName="title"
                      :width="110"></Select>
            </template>
            <template v-else>{{ expenseText(row.expenseType) }}</template>
          </td>
          <td align="right">{{ calcTax(row) | numFormat }}</td>
          <td align="right">{{ calcNet(row) | numFormat }}</td>
          <td v-if="editable">
            <span class="actions" @click="removeRow(index)">删除</span>
          </td>
        </tr>
        <tr v-if="!bills.length">
          <td colspan="11" class="text-center">暂无工资单数据，请"添加员工行"录入。</td>
        </tr>
        </tbody>
        <tfoot>
        <tr>
          <td align="right"><b>合计</b></td>
          <td align="right">{{ sum('gross') | numFormat }}</td>
          <td align="right">{{ sum('bonus') | numFormat }}</td>
          <td align="right">{{ sum('socialPersonal') | numFormat }}</td>
          <td align="right">{{ sum('fundPersonal') | numFormat }}</td>
          <td align="right">{{ sum('socialCompany') | numFormat }}</td>
          <td align="right">{{ sum('fundCompany') | numFormat }}</td>
          <td></td>
          <td align="right">{{ sumTax() | numFormat }}</td>
          <td align="right">{{ sumNet() | numFormat }}</td>
          <td v-if="editable"></td>
        </tr>
        </tfoot>
      </table>
    </div>

    <!--新建期间弹窗-->
    <Modal v-model="showOpenForm" :hasCloseIcon="true" :closeOnMask="false" :middle="true">
      <div slot="header">新建工资期间</div>
      <div class="text-center margin">
        <DatePicker type="month" v-model="openMonth" format="YYYY-MM"></DatePicker>
      </div>
      <div class="text-center">
        <Button color="primary" @click="doOpen" :loading="loading">确认开档</Button>
        <Button @click="showOpenForm=false">取消</Button>
      </div>
    </Modal>

    <!--工资计提：分录预览-->
    <Modal v-model="showAccruePreview" :hasCloseIcon="false" :closeOnMask="false" :middle="true">
      <div slot="header">工资计提预览（{{period ? period.year : ''}}年{{period ? period.month : ''}}月）</div>
      <div style="max-height: 400px; overflow: auto;">
        <table class="salary-table" v-if="accrueLines.length">
          <thead>
          <tr>
            <th>摘要</th>
            <th>科目</th>
            <th>借方</th>
            <th>贷方</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(line, index) in accrueLines" :key="index">
            <td>{{ line.summary }}</td>
            <td>{{ line.subjectName }}</td>
            <td align="right">{{ line.jie | numFormat }}</td>
            <td align="right">{{ line.dai | numFormat }}</td>
          </tr>
          </tbody>
        </table>
        <div class="text-center margin" v-else>当前期间没有可计提的工资数据！</div>
      </div>
      <div class="text-center">
        <Button color="green" @click="doAccrue" :loading="loading" v-if="accrueLines.length">确认计提</Button>
        <Button @click="showAccruePreview=false">取消</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import moment from 'moment';

const emptyRow = {
  employeeId: null,
  gross: 0,
  bonus: 0,
  socialPersonal: 0,
  fundPersonal: 0,
  socialCompany: 0,
  fundCompany: 0,
  expenseType: 1
};

export default {
  name: "Salary",
  data() {
    return {
      periods: [],
      period: null,
      currentPeriodKey: null,
      bills: [],
      employees: [],
      expenseDict: [{key: 1, title: '管理费用'}, {key: 2, title: '销售费用'}],
      showOpenForm: false,
      openMonth: null,
      showAccruePreview: false,
      accrueLines: [],
      loading: false
    }
  },
  computed: {
    periodOptions() {
      return this.periods.map(p => ({key: `${p.year}-${p.month}`, title: `${p.year}年${p.month}月`}));
    },
    editable() {
      return !!this.period && this.period.status === 1;
    }
  },
  watch: {
    currentPeriodKey(key) {
      if (key) {
        this.period = this.periods.find(p => `${p.year}-${p.month}` === key) || null;
      }
    }
  },
  methods: {
    loadPeriods() {
      return this.$api.salary.period.list().then(({data}) => {
        this.periods = data || [];
        if (this.periods.length) {
          if (!this.currentPeriodKey || !this.periods.find(p => `${p.year}-${p.month}` === this.currentPeriodKey)) {
            this.currentPeriodKey = `${this.periods[0].year}-${this.periods[0].month}`;
            this.period = this.periods[0];
          }
        } else {
          this.currentPeriodKey = null;
          this.period = null;
          this.bills = [];
        }
      });
    },
    loadData() {
      this.loading = true;
      let p = this.periods.length ? Promise.resolve() : this.loadPeriods();
      Promise.all([
        p,
        this.employees.length ? Promise.resolve() : this.loadEmployees()
      ]).then(() => {
        if (this.period) {
          return this.$api.salary.bill.load(this.period.year, this.period.month).then(({data}) => {
            this.bills = (data && data.bills) || [];
            if (data && data.employees) {
              this.employees = data.employees;
            }
          });
        }
        this.bills = [];
      }).finally(() => this.loading = false);
    },
    loadEmployees() {
      return this.$api.salary.employee.list().then(({data}) => {
        this.employees = data || [];
      });
    },
    employeeName(employeeId) {
      let employee = this.employees.find(val => val.id === employeeId);
      return employee ? employee.name : employeeId;
    },
    rowEmployees(row) {
      //员工下拉排除已被其他行选用的员工
      return this.employees.filter(emp => emp.id === row.employeeId || !this.bills.some(bill => bill !== row && bill.employeeId === emp.id));
    },
    statusText(status) {
      return status === 3 ? '已发放' : (status === 2 ? '已计提' : '编辑中');
    },
    expenseText(type) {
      let item = this.expenseDict.find(val => val.key === type);
      return item ? item.title : '-';
    },
    num(value) {
      let result = Number(value) || 0;
      return Math.round(result * 100) / 100;
    },
    //2019 年月度个税：应纳税所得额 = 应发合计 - 个人社保 - 个人公积金 - 5000 起征点，分段税率速算扣除（与后端逻辑一致）
    calcTax(row) {
      let taxable = this.num(row.gross) + this.num(row.bonus) - this.num(row.socialPersonal) - this.num(row.fundPersonal) - 5000;
      if (taxable <= 0) {
        return 0;
      }
      let rate, quick;
      if (taxable <= 3000) {
        rate = 0.03;
        quick = 0;
      } else if (taxable <= 12000) {
        rate = 0.10;
        quick = 210;
      } else if (taxable <= 25000) {
        rate = 0.20;
        quick = 1410;
      } else if (taxable <= 35000) {
        rate = 0.25;
        quick = 2660;
      } else if (taxable <= 55000) {
        rate = 0.30;
        quick = 4410;
      } else if (taxable <= 80000) {
        rate = 0.35;
        quick = 7160;
      } else {
        rate = 0.45;
        quick = 15160;
      }
      return this.num(taxable * rate - quick);
    },
    //实发 = 应发合计 - 个人社保 - 个人公积金 - 个税
    calcNet(row) {
      return this.num(this.num(row.gross) + this.num(row.bonus) - this.num(row.socialPersonal) - this.num(row.fundPersonal) - this.calcTax(row));
    },
    sum(field) {
      return this.bills.reduce((total, row) => total + this.num(row[field]), 0);
    },
    sumTax() {
      return this.bills.reduce((total, row) => total + this.calcTax(row), 0);
    },
    sumNet() {
      return this.bills.reduce((total, row) => total + this.calcNet(row), 0);
    },
    addRow() {
      this.bills.push(Object.assign({}, emptyRow));
    },
    removeRow(index) {
      this.bills.splice(index, 1);
    },
    openPeriodForm() {
      this.openMonth = moment().format('YYYY-MM');
      this.showOpenForm = true;
    },
    doOpen() {
      if (!this.openMonth) {
        this.$Message('请选择开档期间！');
        return;
      }
      let date = moment(this.openMonth);
      this.loading = true;
      this.$api.salary.period.open({year: date.year(), month: date.month() + 1}).then(({data}) => {
        this.$Message('开档成功~');
        this.showOpenForm = false;
        return this.loadPeriods().then(() => {
          if (data) {
            this.currentPeriodKey = `${data.year}-${data.month}`;
            this.period = data;
          }
          this.loadData();
        });
      }).catch(() => {
      }).finally(() => this.loading = false);
    },
    removePeriod() {
      this.$Confirm(`确认删除${this.period.year}年${this.period.month}月工资期间？`).then(() => {
        this.$api.salary.period.delete(this.period.id).then(() => {
          this.$Message('删除成功~');
          this.currentPeriodKey = null;
          this.period = null;
          this.bills = [];
          this.loadPeriods().then(() => this.loadData());
        })
      })
    },
    saveBills() {
      for (let row of this.bills) {
        if (!row.employeeId) {
          this.$Message('请为每一行选择员工！');
          return;
        }
      }
      this.loading = true;
      this.$api.salary.bill.save({
        year: this.period.year,
        month: this.period.month,
        bills: this.bills
      }).then(() => {
        this.$Message('保存成功~');
        this.loadData();
      }).catch(() => {
      }).finally(() => this.loading = false);
    },
    applySocial() {
      this.$Confirm('确认将五险一金设置应用到当前工资期间？').then(() => {
        this.loading = true;
        this.$api.salary.bill.applySocial(this.period.id).then(() => {
          this.$Message('应用成功~');
          this.loadData();
        }).catch(() => {
        }).finally(() => this.loading = false);
      });
    },
    accruePreview() {
      this.loading = true;
      this.$api.salary.bill.accruePreview({year: this.period.year, month: this.period.month}).then(({data}) => {
        this.accrueLines = data || [];
        this.showAccruePreview = true;
      }).catch(() => {
      }).finally(() => this.loading = false);
    },
    doAccrue() {
      this.loading = true;
      this.$api.salary.bill.accrue({year: this.period.year, month: this.period.month}).then(() => {
        this.$Message('工资计提成功~');
        this.showAccruePreview = false;
        this.loadData();
      }).catch(() => {
      }).finally(() => this.loading = false);
    },
    doPay() {
      this.$Confirm(`确认发放${this.period.year}年${this.period.month}月工资？将生成发放凭证，操作不可恢复！`).then(() => {
        this.loading = true;
        this.$api.salary.bill.pay(this.period.id).then(() => {
          this.$Message('工资发放成功~');
          this.loadData();
        }).catch(() => {
        }).finally(() => this.loading = false);
      })
    }
  },
  mounted() {
    this.loadPeriods().then(() => this.loadData());
  }
}
</script>

<style lang="less" scoped>
.salary-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    border-bottom: 1px solid #eee;
    padding: 6px 8px;
  }

  th {
    background: #f8f8f8;
  }
}
</style>
